import { AccountsService } from 'base/accounts_service';
import { AnalyticsService } from 'base/analytics';
import { formatDate } from 'base/helpers';
import { Avatar } from 'base/ui/avatar';
import { Button } from 'base/ui/button';
import { IconGift, IconLink } from 'base/ui/icons';
import { Inline, Rows } from 'base/ui/layout';
import { Link } from 'base/ui/link';
import { LoadingDots } from 'base/ui/loading_dots';
import { ToastQueue } from 'base/ui/toast';
import {
  TextRegular,
  TextRegularLight,
  TextSmall,
  TextSmallLight,
  Title0,
  Title5,
} from 'base/ui/typography';
import { useFetchData } from 'base/use_fetch_data';
import { FeatureFlagsStore } from 'blocks/flag_overrides';
import communityImage from 'blocks/manage_account/community-support.file.png';
import { useReferralLink } from 'blocks/use_referral_link';
import React from 'react';
import styles from './manage_referral.css';

export const ManageReferral = ({
  accountsService,
  analyticsService,
  toastQueue,
  featureFlagsStore,
}: {
  accountsService: AccountsService;
  analyticsService: AnalyticsService;
  toastQueue: ToastQueue;
  featureFlagsStore: FeatureFlagsStore;
}) => {
  const linkDataState = useReferralLink({
    accountsService,
    enabled: true,
  });
  const credit = useFetchData(() => accountsService.getBalance());
  const rewards = useFetchData(() => accountsService.getReferralRewards());
  const { url } = linkDataState;
  return (
    <div className={styles.container}>
      <div className={styles.headerImage}>
        <img className={styles.imageContent} src={communityImage} alt="" />
      </div>
      <Rows spacing={32}>
        <div className={styles.headerContainer}>
          <Rows spacing={20}>
            <Title0>Invite friends & earn rewards</Title0>
            <TextRegularLight>
              Refer a friend and earn $10 credit for every paying user. They’ll
              get 50% off their first month on Starter or Pro plans.{' '}
              <b>Start sharing today!</b>
            </TextRegularLight>
          </Rows>
        </div>
        <Rows spacing={40}>
          <div className={styles.balanceContainer}>
            <Rows spacing={8}>
              <div>
                <div className={styles.creditContainer}>
                  {!credit.data ? (
                    <LoadingDots />
                  ) : (
                    <Title5 color="gradient">{`Available credit: $${credit.data.balance}`}</Title5>
                  )}
                </div>
              </div>
              <TextSmallLight>
                This will be applied to your next invoice.
              </TextSmallLight>
            </Rows>
          </div>
          {featureFlagsStore.get('enableReferral') && (
            <Rows spacing={16}>
              <Title5>Share your link</Title5>
              <div className={styles.referralLinkContent}>
                {!url ? (
                  <LoadingDots />
                ) : (
                  <>
                    <div className={styles.linkContainer}>
                      <TextRegularLight lineClamp={1}>{url}</TextRegularLight>
                    </div>
                    <div className={styles.buttonContainer}>
                      <Button
                        variant="primary"
                        size="large"
                        icon={IconLink}
                        label="Copy Link"
                        fullWidth
                        onClick={() => {
                          analyticsService.track({
                            event: 'referral_flow:link_copied',
                            link: url,
                          });
                          navigator.clipboard.writeText(url);
                          toastQueue.push({ label: 'Copied to clipboard' });
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </Rows>
          )}
          <Rows spacing={0}>
            <Title5>Invited friends</Title5>
            {rewards.data?.rewards?.length === 0 ? (
              <div className={styles.emptySection}>
                <div className={styles.emptySectionContent}>
                  <Rows fixedWidth xalign="center" yalign="center" spacing={8}>
                    <IconGift width={32} height={32} color="black" />
                    <TextRegular align="center">
                      Earn rewards by inviting friends
                    </TextRegular>
                    <TextSmallLight align="center" color="grey">
                      No invites yet? Send one now and get rewarded when friends
                      join Relume.
                    </TextSmallLight>
                  </Rows>
                </div>
              </div>
            ) : (
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>
                      <TextSmall color="grey">Friends name</TextSmall>
                    </th>
                    <th>
                      <TextSmall color="grey">Signup date</TextSmall>
                    </th>
                    <th>
                      <TextSmall color="grey">Reward</TextSmall>
                    </th>
                    <th>
                      <TextSmall color="grey">Reward issued</TextSmall>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rewards.data?.rewards?.map((offer, i) => (
                    <tr key={i}>
                      <td>
                        <Inline spacing={8} yalign="center">
                          <Avatar
                            size="medium"
                            shape="circle"
                            avatar={offer.avatar}
                            firstName={offer.referredAccountName}
                            color={offer.avatarColor}
                          />
                          <TextRegularLight color="grey">
                            {offer.referredAccountName}
                          </TextRegularLight>
                        </Inline>
                      </td>
                      <td>
                        <TextRegularLight color="grey">
                          {formatDate(offer.createdAt, 'dd/mm/yyyy')}
                        </TextRegularLight>
                      </td>
                      <td>
                        <TextRegularLight color="grey">
                          {offer.rewardCredit}
                        </TextRegularLight>
                      </td>
                      <td>
                        <TextRegularLight color="grey">
                          {offer.converted ? 'Issued' : 'Pending'}
                        </TextRegularLight>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Rows>
          <Rows fixedWidth xalign="center">
            <TextSmallLight color="grey">
              Sharing your link means you agree to our{' '}
              <Link href="https://www.relume.io/legal/terms">
                Terms & Conditions.
              </Link>{' '}
            </TextSmallLight>
          </Rows>
        </Rows>
      </Rows>
    </div>
  );
};
